import {IMetaDataServiceImpl} from '../services/metadata/imetadataserviceimpl';
import {FieldPathUtils} from '../utils/fieldpath-utils';
import {FieldMetaTypeGuards} from './FieldMetaTypeGuards';
import { IBullhornFieldMeta, IEntityMeta } from './imetadata';

enum BullhornOptionsTypeEntityMapping {
  BusinessSector = 'BusinessSector',
  Candidate = 'Candidate',
  Category = 'Category',
  Certification = 'Certification',
  Client = 'ClientContact',
  ClientCorporation = 'ClientCorporation',
  CorporationDepartment = 'CorporationDepartment',
  HousingComplex = 'HousingComplex',
  HousingComplexUnit = 'HousingComplexUnit',
  CorporateUser = 'CorporateUser',
  JobOrder = 'JobOrder',
  Shift = 'Shift',
  Skill = 'Skill',
  Specialty = 'Specialty',
  WorkersComp = 'WorkersCompensationRate',
  WorkersCompensation = 'WorkersCompensation',
}

/**
 * Associated fields that must remain reachable even when they sit beyond the configured
 * maximum depth, keyed by the entity that owns them.
 *
 * Bullhorn Placement Rate Cards bury Earn Codes deep from Placement:
 * Placement > PlacementRateCard > Versions > PlacementRateCardLineGroup >
 * PlacementRateCardLine > earnCode. Raising the global max depth instead would fan the
 * eager pull out across every branch of every entity, which is far too slow and costly.
 */
const BullhornDeepFieldsByEntity: Readonly<Record<string, ReadonlySet<string>>> = {
  placementratecardline: new Set(['earncode']),
};

/** Hard ceiling on how far past maxDepth any single field may be extended. */
const MAX_EXTRA_DEPTH = 1;

export class BullhornMetaDataServiceImpl implements IMetaDataServiceImpl<IBullhornFieldMeta> {
  promoteCompositeFields(field: IBullhornFieldMeta): void {
    if (!this.isCompositeField(field)) {
      return;
    }

    if (field.fields && field.fields.length > 0) {
      throw new Error(`Composite field '${field.path}' already has fields.`);
    }

    const childFields: IBullhornFieldMeta[] = [];
    field.childrenOfComposite?.forEach((cf) => {
      childFields.push({ ...cf });
    });

    this.setChildren(field, childFields);
  }

  private setChildren(parent: IBullhornFieldMeta, children: IBullhornFieldMeta[]): void {
    parent.fields = children;
    this.setParent(children, parent);
  }

  private setParent(fields: IBullhornFieldMeta[], parent?: IBullhornFieldMeta): void {
    fields.forEach((field) => {
      field.parent = parent;
      if (parent) { field.path = field.parent?.path + '.' + field.path.split('.').pop(); } // child path to parent
      if (field.fields) {
        const bullhornFields = field.fields.filter(FieldMetaTypeGuards.isBullhorn);
        this.setParent(bullhornFields, field);
      }
    });
  }

  findAssociatedEntity(field: IBullhornFieldMeta, assocEntityList: { entityType: string; result: Readonly<IEntityMeta> }[]) {
    return this.isPickerField(field)
      ? assocEntityList.find((e) => e.entityType.toLowerCase() === this.getPickerFieldAssociatedEntity(field)?.toLowerCase())
      : assocEntityList.find((e) => e.entityType.toLowerCase() === field.associatedEntity?.toLowerCase());
  }

  updatePickerFieldMeta(field: IBullhornFieldMeta): void {
    if (this.isAssociatedField(field)) {
      return;
    }
    if (!this.isPickerField(field)) {
      return;
    }
    field.type = field.multiValue ? 'to-many' : 'to-one';
    field.isPickerField = true;
  }

  getAssociatedEntity(field: IBullhornFieldMeta): string {
    if (this.isPickerField(field)) {
      field.associatedEntity = field.associatedEntity ?? this.getPickerFieldAssociatedEntity(field);
    } else {
      field.associatedEntity = field.associatedEntity ?? field.path;
    }
    return field.associatedEntity;
  }

  getExtraDepth(field: IBullhornFieldMeta, parentEntityType: string): number {
    if (!this.isAssociatedField(field)) {
      return 0;
    }
    return this.isDeepField(field, parentEntityType) ? MAX_EXTRA_DEPTH : 0;
  }

  private isPickerField(field: IBullhornFieldMeta): boolean {
    return (field.optionsType as keyof typeof BullhornOptionsTypeEntityMapping) in BullhornOptionsTypeEntityMapping;
  }

  private isAssociatedField(field: IBullhornFieldMeta): boolean {
    return field.type === 'to-many' || field.type === 'to-one';
  }

  private isCompositeField(field: IBullhornFieldMeta): boolean {
    return field.type === 'composite';
  }

  private isDeepField(field: IBullhornFieldMeta, parentEntityType: string): boolean {
    const deepFields = BullhornDeepFieldsByEntity[parentEntityType?.toLowerCase()];
    return deepFields?.has(FieldPathUtils.leaf(field.path).toLowerCase()) === true;
  }

  private getPickerFieldAssociatedEntity(field: IBullhornFieldMeta): BullhornOptionsTypeEntityMapping {
    const optionsType = field.optionsType as keyof typeof BullhornOptionsTypeEntityMapping;
    return BullhornOptionsTypeEntityMapping[optionsType];
  }

}
